import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { employeeAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Button } from '../../components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Users,
    Search,
    Filter,
    TrendingUp,
    Heart,
    AlertTriangle,
    Mail,
    Building2,
    Calendar,
    X,
    Plus,
    Trash2,
    Copy,
    CheckCircle,
    Pencil
} from 'lucide-react';
import { getScoreColor, getSeverityColor, formatDate } from '../../lib/utils';

const EmployeeCard = ({ employee, onClick }) => {
    const getInitials = (name) => {
        if (!name) return 'U';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    const getRiskBadge = (risk) => {
        switch (risk) {
            case 'high':
            case 'critical':
                return <Badge className="bg-red-100 text-red-800 border-red-200">High Risk</Badge>;
            case 'medium':
                return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Medium Risk</Badge>;
            default:
                return null;
        }
    };

    return (
        <Card 
            className="bento-card-interactive cursor-pointer hover:border-indigo-200"
            onClick={() => onClick(employee)}
            data-testid={`employee-card-${employee.id}`}
        >
            <CardContent className="p-4">
                <div className="flex items-start gap-4">
                    <Avatar className="w-12 h-12">
                        <AvatarFallback className="bg-indigo-100 text-indigo-600 font-medium">
                            {getInitials(employee.name)}
                        </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium text-stone-900 truncate">{employee.name}</h4>
                            {getRiskBadge(employee.attrition_risk)}
                        </div>
                        <p className="text-sm text-stone-500">{employee.position}</p>
                        <p className="text-xs text-stone-400">{employee.department}</p>
                    </div>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-3">
                    <div>
                        <div className="flex items-center gap-1 text-xs text-stone-500 mb-1">
                            <TrendingUp className="w-3 h-3" />
                            Sentiment
                        </div>
                        <Progress value={(employee.sentiment_score || 0) * 100} className="h-1.5" />
                        <p className={`text-xs font-mono mt-1 ${getScoreColor(employee.sentiment_score || 0)}`}>
                            {((employee.sentiment_score || 0) * 100).toFixed(0)}%
                        </p>
                    </div>
                    <div>
                        <div className="flex items-center gap-1 text-xs text-stone-500 mb-1">
                            <Users className="w-3 h-3" />
                            Engagement
                        </div>
                        <Progress value={(employee.engagement_score || 0) * 100} className="h-1.5" />
                        <p className={`text-xs font-mono mt-1 ${getScoreColor(employee.engagement_score || 0)}`}>
                            {((employee.engagement_score || 0) * 100).toFixed(0)}%
                        </p>
                    </div>
                    <div>
                        <div className="flex items-center gap-1 text-xs text-stone-500 mb-1">
                            <Heart className="w-3 h-3" />
                            Well-being
                        </div>
                        <Progress value={(employee.wellbeing_score || 0) * 100} className="h-1.5" />
                        <p className={`text-xs font-mono mt-1 ${getScoreColor(employee.wellbeing_score || 0)}`}>
                            {((employee.wellbeing_score || 0) * 100).toFixed(0)}%
                        </p>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default function EmployeesPage() {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all');
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    
    // Add employee state
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [addingEmployee, setAddingEmployee] = useState(false);
    const [newEmployee, setNewEmployee] = useState({
        name: '',
        email: '',
        password: '',
        department: 'Unassigned',
        position: 'Employee'
    });
    const [createdEmployee, setCreatedEmployee] = useState(null);
    const [copiedPassword, setCopiedPassword] = useState(false);

    // Edit employee state
    const [editingEmployee, setEditingEmployee] = useState(null);
    const [editForm, setEditForm] = useState({ name: '', department: '', position: '' });
    const [savingEdit, setSavingEdit] = useState(false);

    useEffect(() => {
        fetchEmployees();
    }, []);

    const fetchEmployees = async () => {
        try {
            setLoading(true);
            const response = await employeeAPI.getAll();
            setEmployees(response.data || []);
        } catch (error) {
            console.error('Failed to fetch employees:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddEmployee = async () => {
        if (!newEmployee.name || !newEmployee.email || !newEmployee.password) return;
        
        try {
            setAddingEmployee(true);
            const response = await employeeAPI.create(newEmployee);
            setCreatedEmployee(response.data.employee);
            await fetchEmployees();
        } catch (error) {
            console.error('Failed to create employee:', error);
            alert(error.response?.data?.detail || 'Failed to create employee');
        } finally {
            setAddingEmployee(false);
        }
    };

    const handleDeleteEmployee = async (employeeId) => {
        if (!confirm('Are you sure you want to delete this employee? This action cannot be undone.')) return;
        
        try {
            await employeeAPI.delete(employeeId);
            setSelectedEmployee(null);
            await fetchEmployees();
        } catch (error) {
            console.error('Failed to delete employee:', error);
        }
    };

    const openEditDialog = (employee) => {
        setEditingEmployee(employee);
        setEditForm({
            name: employee.name || '',
            department: employee.department || 'Unassigned',
            position: employee.position || 'Employee'
        });
    };

    const handleSaveEdit = async () => {
        if (!editingEmployee) return;
        
        try {
            setSavingEdit(true);
            await employeeAPI.update(editingEmployee.id, editForm);
            setEditingEmployee(null);
            setSelectedEmployee(null);
            await fetchEmployees();
        } catch (error) {
            console.error('Failed to update employee:', error);
            alert(error.response?.data?.detail || 'Failed to update employee');
        } finally {
            setSavingEdit(false);
        }
    };

    const copyPassword = () => {
        navigator.clipboard.writeText(createdEmployee?.temporary_password || '');
        setCopiedPassword(true);
        setTimeout(() => setCopiedPassword(false), 2000);
    };

    const closeAddDialog = () => {
        setShowAddDialog(false);
        setNewEmployee({ name: '', email: '', password: '', department: 'Unassigned', position: 'Employee' });
        setCreatedEmployee(null);
    };

    const generatePassword = () => {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
        let password = '';
        for (let i = 0; i < 10; i++) {
            password += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        setNewEmployee({ ...newEmployee, password });
    };

    const getInitials = (name) => {
        if (!name) return 'U';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    const getRiskLabel = (risk) => {
        switch (risk) {
            case 'critical': return { label: 'Critical Risk', color: 'text-red-600 bg-red-100' };
            case 'high': return { label: 'High Risk', color: 'text-orange-600 bg-orange-100' };
            case 'medium': return { label: 'Medium Risk', color: 'text-amber-600 bg-amber-100' };
            case 'low': return { label: 'Low Risk', color: 'text-emerald-600 bg-emerald-100' };
            default: return { label: 'Unknown', color: 'text-stone-600 bg-stone-100' };
        }
    };

    const filteredEmployees = employees.filter(emp => {
        const matchesSearch = emp.name.toLowerCase().includes(search.toLowerCase()) ||
                            emp.email.toLowerCase().includes(search.toLowerCase()) ||
                            emp.department.toLowerCase().includes(search.toLowerCase());
        
        if (filter === 'all') return matchesSearch;
        if (filter === 'at-risk') return matchesSearch && ['high', 'critical'].includes(emp.attrition_risk);
        if (filter === 'low-engagement') return matchesSearch && emp.engagement_score < 0.4;
        if (filter === 'low-wellbeing') return matchesSearch && emp.wellbeing_score < 0.4;
        return matchesSearch;
    });

    const stats = {
        total: employees.length,
        atRisk: employees.filter(e => ['high', 'critical'].includes(e.attrition_risk)).length,
        lowEngagement: employees.filter(e => e.engagement_score < 0.4).length,
        lowWellbeing: employees.filter(e => e.wellbeing_score < 0.4).length,
    };

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-stone-500">Loading employees...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-8" data-testid="employees-page">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                {/* Header */}
                <div className="flex items-start justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold font-heading text-stone-900 flex items-center gap-3">
                            <Users className="w-8 h-8 text-indigo-600" />
                            Employee Directory
                        </h1>
                        <p className="text-stone-500 mt-1">
                            View and manage employee profiles, scores, and risk assessments.
                        </p>
                    </div>
                    <Button 
                        onClick={() => setShowAddDialog(true)}
                        className="bg-indigo-600 hover:bg-indigo-700"
                        data-testid="add-employee-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Employee
                    </Button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <button
                        onClick={() => setFilter('all')}
                        className={`p-4 rounded-xl border transition-all ${
                            filter === 'all' ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-stone-200 hover:bg-stone-50'
                        }`}
                    >
                        <p className="text-2xl font-bold font-heading text-stone-900">{stats.total}</p>
                        <p className="text-sm text-stone-500">Total Employees</p>
                    </button>
                    <button
                        onClick={() => setFilter('at-risk')}
                        className={`p-4 rounded-xl border transition-all ${
                            filter === 'at-risk' ? 'bg-red-50 border-red-200' : 'bg-white border-stone-200 hover:bg-stone-50'
                        }`}
                    >
                        <p className="text-2xl font-bold font-heading text-red-600">{stats.atRisk}</p>
                        <p className="text-sm text-stone-500">At Risk</p>
                    </button>
                    <button
                        onClick={() => setFilter('low-engagement')}
                        className={`p-4 rounded-xl border transition-all ${
                            filter === 'low-engagement' ? 'bg-amber-50 border-amber-200' : 'bg-white border-stone-200 hover:bg-stone-50'
                        }`}
                    >
                        <p className="text-2xl font-bold font-heading text-amber-600">{stats.lowEngagement}</p>
                        <p className="text-sm text-stone-500">Low Engagement</p>
                    </button>
                    <button
                        onClick={() => setFilter('low-wellbeing')}
                        className={`p-4 rounded-xl border transition-all ${
                            filter === 'low-wellbeing' ? 'bg-orange-50 border-orange-200' : 'bg-white border-stone-200 hover:bg-stone-50'
                        }`}
                    >
                        <p className="text-2xl font-bold font-heading text-orange-600">{stats.lowWellbeing}</p>
                        <p className="text-sm text-stone-500">Low Well-being</p>
                    </button>
                </div>

                {/* Search */}
                <div className="relative mb-6">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                    <Input
                        placeholder="Search by name, email, or department..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-12 h-12 rounded-xl bg-white"
                        data-testid="employee-search"
                    />
                </div>

                {/* Employee Grid */}
                {filteredEmployees.length === 0 ? (
                    <Card className="bento-card">
                        <CardContent className="p-12 text-center">
                            <div className="w-16 h-16 rounded-2xl bg-stone-100 flex items-center justify-center mx-auto mb-4">
                                <Users className="w-8 h-8 text-stone-400" />
                            </div>
                            <h3 className="text-lg font-medium text-stone-900 mb-2">No employees found</h3>
                            <p className="text-stone-500">
                                {search ? 'Try a different search term.' : 'Register employees to see them here.'}
                            </p>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredEmployees.map((employee) => (
                            <EmployeeCard 
                                key={employee.id} 
                                employee={employee}
                                onClick={setSelectedEmployee}
                            />
                        ))}
                    </div>
                )}
            </motion.div>

            {/* Employee Detail Modal */}
            <Dialog open={!!selectedEmployee} onOpenChange={() => setSelectedEmployee(null)}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-4">
                            <Avatar className="w-14 h-14">
                                <AvatarFallback className="bg-indigo-100 text-indigo-600 font-medium text-lg">
                                    {getInitials(selectedEmployee?.name)}
                                </AvatarFallback>
                            </Avatar>
                            <div>
                                <h2 className="text-xl font-bold">{selectedEmployee?.name}</h2>
                                <p className="text-sm text-stone-500">{selectedEmployee?.position} • {selectedEmployee?.department}</p>
                            </div>
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedEmployee && (
                        <div className="space-y-6 mt-4">
                            {/* Contact Info */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="flex items-center gap-2 text-sm text-stone-600">
                                    <Mail className="w-4 h-4 text-stone-400" />
                                    {selectedEmployee.email}
                                </div>
                                <div className="flex items-center gap-2 text-sm text-stone-600">
                                    <Building2 className="w-4 h-4 text-stone-400" />
                                    {selectedEmployee.department}
                                </div>
                                <div className="flex items-center gap-2 text-sm text-stone-600">
                                    <Calendar className="w-4 h-4 text-stone-400" />
                                    Joined {formatDate(selectedEmployee.hire_date)}
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                    <AlertTriangle className="w-4 h-4 text-stone-400" />
                                    <Badge className={getRiskLabel(selectedEmployee.attrition_risk).color}>
                                        {getRiskLabel(selectedEmployee.attrition_risk).label}
                                    </Badge>
                                </div>
                            </div>

                            {/* Scores */}
                            <div className="space-y-4">
                                <h4 className="font-medium text-stone-900">Performance Metrics</h4>
                                <div className="space-y-3">
                                    <div>
                                        <div className="flex justify-between text-sm mb-1">
                                            <span className="text-stone-600 flex items-center gap-2">
                                                <TrendingUp className="w-4 h-4" /> Sentiment Score
                                            </span>
                                            <span className={`font-medium ${getScoreColor(selectedEmployee.sentiment_score || 0)}`}>
                                                {((selectedEmployee.sentiment_score || 0) * 100).toFixed(0)}%
                                            </span>
                                        </div>
                                        <Progress value={(selectedEmployee.sentiment_score || 0) * 100} className="h-2" />
                                    </div>
                                    <div>
                                        <div className="flex justify-between text-sm mb-1">
                                            <span className="text-stone-600 flex items-center gap-2">
                                                <Users className="w-4 h-4" /> Engagement Score
                                            </span>
                                            <span className={`font-medium ${getScoreColor(selectedEmployee.engagement_score || 0)}`}>
                                                {((selectedEmployee.engagement_score || 0) * 100).toFixed(0)}%
                                            </span>
                                        </div>
                                        <Progress value={(selectedEmployee.engagement_score || 0) * 100} className="h-2" />
                                    </div>
                                    <div>
                                        <div className="flex justify-between text-sm mb-1">
                                            <span className="text-stone-600 flex items-center gap-2">
                                                <Heart className="w-4 h-4" /> Well-being Score
                                            </span>
                                            <span className={`font-medium ${getScoreColor(selectedEmployee.wellbeing_score || 0)}`}>
                                                {((selectedEmployee.wellbeing_score || 0) * 100).toFixed(0)}%
                                            </span>
                                        </div>
                                        <Progress value={(selectedEmployee.wellbeing_score || 0) * 100} className="h-2" />
                                    </div>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-3 pt-4 border-t">
                                <Button 
                                    variant="outline" 
                                    className="flex-1"
                                    onClick={() => setSelectedEmployee(null)}
                                >
                                    Close
                                </Button>
                                <Button 
                                    variant="outline"
                                    onClick={() => openEditDialog(selectedEmployee)}
                                    data-testid="edit-employee-btn"
                                >
                                    <Pencil className="w-4 h-4 mr-2" />
                                    Edit
                                </Button>
                                <Button 
                                    variant="destructive"
                                    onClick={() => handleDeleteEmployee(selectedEmployee.id)}
                                    data-testid="delete-employee-btn"
                                >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Edit Employee Dialog */}
            <Dialog open={!!editingEmployee} onOpenChange={() => setEditingEmployee(null)}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Edit Employee</DialogTitle>
                        <DialogDescription>
                            Update employee information.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="edit-name">Full Name</Label>
                            <Input
                                id="edit-name"
                                value={editForm.name}
                                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Department</Label>
                            <Select 
                                value={editForm.department} 
                                onValueChange={(val) => setEditForm({ ...editForm, department: val })}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Unassigned">Unassigned</SelectItem>
                                    <SelectItem value="Engineering">Engineering</SelectItem>
                                    <SelectItem value="Marketing">Marketing</SelectItem>
                                    <SelectItem value="Sales">Sales</SelectItem>
                                    <SelectItem value="HR">HR</SelectItem>
                                    <SelectItem value="Finance">Finance</SelectItem>
                                    <SelectItem value="Operations">Operations</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="edit-position">Position</Label>
                            <Input
                                id="edit-position"
                                value={editForm.position}
                                onChange={(e) => setEditForm({ ...editForm, position: e.target.value })}
                            />
                        </div>
                        
                        <div className="flex gap-3 pt-4">
                            <Button 
                                variant="outline" 
                                onClick={() => setEditingEmployee(null)}
                                className="flex-1"
                            >
                                Cancel
                            </Button>
                            <Button 
                                onClick={handleSaveEdit}
                                disabled={savingEdit || !editForm.name}
                                className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                                data-testid="save-edit-btn"
                            >
                                {savingEdit ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Add Employee Dialog */}
            <Dialog open={showAddDialog} onOpenChange={closeAddDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Add New Employee</DialogTitle>
                        <DialogDescription>
                            Create an employee account. Share the login credentials with the employee.
                        </DialogDescription>
                    </DialogHeader>
                    
                    {createdEmployee ? (
                        <div className="space-y-4">
                            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                                <div className="flex items-center gap-2 mb-2">
                                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                                    <span className="font-medium text-emerald-800">Employee Created Successfully!</span>
                                </div>
                                <p className="text-sm text-emerald-700">Share these login credentials with the employee:</p>
                            </div>
                            
                            <div className="space-y-3 p-4 bg-stone-50 rounded-lg">
                                <div>
                                    <Label className="text-xs text-stone-500">Name</Label>
                                    <p className="font-medium">{createdEmployee.name}</p>
                                </div>
                                <div>
                                    <Label className="text-xs text-stone-500">Email</Label>
                                    <p className="font-medium">{createdEmployee.email}</p>
                                </div>
                                <div>
                                    <Label className="text-xs text-stone-500">Temporary Password</Label>
                                    <div className="flex items-center gap-2">
                                        <code className="flex-1 p-2 bg-white border rounded font-mono text-sm">
                                            {createdEmployee.temporary_password}
                                        </code>
                                        <Button 
                                            variant="outline" 
                                            size="sm"
                                            onClick={copyPassword}
                                        >
                                            {copiedPassword ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                                        </Button>
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-xs text-stone-500">Department</Label>
                                    <p className="font-medium">{createdEmployee.department}</p>
                                </div>
                            </div>
                            
                            <Button onClick={closeAddDialog} className="w-full">
                                Done
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="emp-name">Full Name *</Label>
                                <Input
                                    id="emp-name"
                                    placeholder="John Smith"
                                    value={newEmployee.name}
                                    onChange={(e) => setNewEmployee({ ...newEmployee, name: e.target.value })}
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="emp-email">Email *</Label>
                                <Input
                                    id="emp-email"
                                    type="email"
                                    placeholder="john@company.com"
                                    value={newEmployee.email}
                                    onChange={(e) => setNewEmployee({ ...newEmployee, email: e.target.value })}
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="emp-password">Password *</Label>
                                <div className="flex gap-2">
                                    <Input
                                        id="emp-password"
                                        type="text"
                                        placeholder="Temporary password"
                                        value={newEmployee.password}
                                        onChange={(e) => setNewEmployee({ ...newEmployee, password: e.target.value })}
                                    />
                                    <Button 
                                        type="button" 
                                        variant="outline"
                                        onClick={generatePassword}
                                    >
                                        Generate
                                    </Button>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Department</Label>
                                    <Select 
                                        value={newEmployee.department} 
                                        onValueChange={(val) => setNewEmployee({ ...newEmployee, department: val })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="Unassigned">Unassigned</SelectItem>
                                            <SelectItem value="Engineering">Engineering</SelectItem>
                                            <SelectItem value="Marketing">Marketing</SelectItem>
                                            <SelectItem value="Sales">Sales</SelectItem>
                                            <SelectItem value="HR">HR</SelectItem>
                                            <SelectItem value="Finance">Finance</SelectItem>
                                            <SelectItem value="Operations">Operations</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Position</Label>
                                    <Input
                                        placeholder="Job title"
                                        value={newEmployee.position}
                                        onChange={(e) => setNewEmployee({ ...newEmployee, position: e.target.value })}
                                    />
                                </div>
                            </div>
                            
                            <div className="flex gap-3 pt-4">
                                <Button 
                                    variant="outline" 
                                    onClick={closeAddDialog}
                                    className="flex-1"
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    onClick={handleAddEmployee}
                                    disabled={addingEmployee || !newEmployee.name || !newEmployee.email || !newEmployee.password}
                                    className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                                    data-testid="confirm-add-employee-btn"
                                >
                                    {addingEmployee ? 'Creating...' : 'Create Employee'}
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
